import { useEffect, useState } from 'react';

export interface DiceRollResult {
  hope: number;
  fear: number;
  advDie?: number;
  modifier: number;
  total: number;
  verdict: 'crit' | 'hope' | 'fear';
}

export interface DiceRollerProps {
  onClose?: () => void;
  /** Викликається з фінальним результатом після завершення анімації */
  onRollComplete?: (r: DiceRollResult) => void;
  /** Контекст — заголовок над дайсом («Атака мечем», тощо) */
  label?: string;
  /** Початковий модифікатор */
  initialModifier?: number;
  /** Якщо передано — кидає одразу при відкритті */
  autoRoll?: boolean;
  /**
   * Playback-режим: показує задані значення без рандому, з анімацією settling.
   * Усі клієнти бачать той самий бросок.
   */
  playback?: { hope: number; fear: number; advDie?: number; modifier?: number };
}

/**
 * Daggerheart Duality Dice: 2d12 (Hope + Fear) + optional d6 advantage/disadvantage.
 *   hope > fear  → «З Надією»   (гравцю +1 Hope)
 *   fear > hope  → «У Страху»   (GM   +1 Fear)
 *   hope == fear → Критичний успіх
 */
export function DiceRoller({ onClose, onRollComplete, label, initialModifier = 0, autoRoll = false, playback }: DiceRollerProps): JSX.Element {
  const [hope, setHope] = useState<number | null>(playback?.hope ?? null);
  const [fear, setFear] = useState<number | null>(playback?.fear ?? null);
  const [advDie, setAdvDie] = useState<number | null>(playback?.advDie ?? null);
  const [rolling, setRolling] = useState(false);
  const [modifier, setModifier] = useState(playback?.modifier ?? initialModifier);
  const [adv, setAdv] = useState<'none' | 'advantage' | 'disadvantage'>(
    playback?.advDie ? (playback.advDie > 0 ? 'advantage' : 'disadvantage') : 'none',
  );

  const roll = (): void => {
    setRolling(true);
    // Анімація: блимаємо випадковими значеннями ~600ms
    const interval = setInterval(() => {
      setHope(1 + Math.floor(Math.random() * 12));
      setFear(1 + Math.floor(Math.random() * 12));
      if (adv !== 'none') setAdvDie(1 + Math.floor(Math.random() * 6));
    }, 70);
    setTimeout(() => {
      clearInterval(interval);
      const finalHope = 1 + Math.floor(Math.random() * 12);
      const finalFear = 1 + Math.floor(Math.random() * 12);
      const finalAdv = adv === 'advantage'
        ? 1 + Math.floor(Math.random() * 6)
        : adv === 'disadvantage'
          ? -(1 + Math.floor(Math.random() * 6))
          : null;
      setHope(finalHope);
      setFear(finalFear);
      setAdvDie(finalAdv);
      setRolling(false);
      if (onRollComplete) {
        const advBonus = finalAdv ?? 0;
        const total = finalHope + finalFear + modifier + advBonus;
        const verdict: 'crit' | 'hope' | 'fear' = finalHope === finalFear ? 'crit' : finalHope > finalFear ? 'hope' : 'fear';
        onRollComplete({ hope: finalHope, fear: finalFear, advDie: finalAdv ?? undefined, modifier, total, verdict });
      }
    }, 600);
  };

  // Автоматично кидаємо при відкритті (для weapon-кнопок)
  useEffect(() => {
    if (autoRoll && !playback && hope === null) {
      // Маленька затримка щоб модал встиг відрендеритися
      const t = setTimeout(() => roll(), 100);
      return () => clearTimeout(t);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRoll, playback]);

  // Playback: грає settle-анімацію один раз
  useEffect(() => {
    if (playback) {
      setRolling(true);
      const t = setTimeout(() => setRolling(false), 600);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [playback]);

  const reset = (): void => {
    setHope(null); setFear(null); setAdvDie(null); setRolling(false);
    setModifier(0); setAdv('none');
  };

  const result = (() => {
    if (hope === null || fear === null) return null;
    const isCrit = hope === fear;
    const withHope = !isCrit && hope > fear;
    const advBonus = adv === 'advantage' && advDie ? advDie : adv === 'disadvantage' && advDie ? -advDie : 0;
    const total = hope + fear + modifier + advBonus;
    return {
      total,
      verdict: isCrit
        ? { head: 'Критичний успіх', sub: 'Подвійні Hope · Stress -1', tone: 'crit' as const }
        : withHope
          ? { head: 'З Надією', sub: 'Гравець отримує +1 Hope', tone: 'hope' as const }
          : { head: 'У Страху', sub: 'GM отримує +1 Fear', tone: 'fear' as const },
      advBonus,
    };
  })();

  const toneColor = (t: 'crit' | 'hope' | 'fear'): string =>
    t === 'crit' ? 'var(--gold-bright)' : t === 'hope' ? 'var(--gold)' : 'var(--blood-bright)';

  return (
    <div style={{ fontFamily: 'Spectral, serif', color: 'var(--text)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 16 }}>
        <h2 style={{ fontFamily: 'Cormorant Garamond, serif', color: 'var(--gold-bright)', margin: 0, fontSize: 20 }}>
          {label ?? 'Hope · Fear'}
        </h2>
        {onClose && (
          <button onClick={onClose} style={{
            background: 'transparent', border: 'none', color: 'var(--text-dim)',
            fontSize: 20, cursor: 'pointer', padding: '0 8px',
          }}>×</button>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', gap: 18, marginBottom: 18 }}>
        <Die value={hope} type="hope" rolling={rolling} />
        <Die value={fear} type="fear" rolling={rolling} />
        {adv !== 'none' && <Die value={advDie} type="adv" rolling={rolling} small />}
      </div>

      {result && !rolling && (
        <div style={{
          padding: 16,
          margin: '0 0 16px',
          background: 'var(--bg-card)',
          border: `1px solid ${toneColor(result.verdict.tone)}`,
          borderRadius: 4,
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          gap: 12,
          alignItems: 'center',
        }}>
          <div>
            <div style={{
              fontFamily: 'Cormorant Garamond, serif',
              fontSize: 18,
              color: toneColor(result.verdict.tone),
              fontWeight: 600,
            }}>{result.verdict.head}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
              {result.verdict.sub}
            </div>
            <div style={{ marginTop: 8, fontFamily: 'Spline Sans Mono, monospace', fontSize: 11, color: 'var(--text-dim)' }}>
              {hope} (hope) + {fear} (fear)
              {result.advBonus !== 0 && ` ${result.advBonus > 0 ? '+' : ''}${result.advBonus} (${adv === 'advantage' ? 'adv' : 'dis'})`}
              {modifier !== 0 && ` ${modifier > 0 ? '+' : ''}${modifier} (mod)`}
            </div>
          </div>
          <div style={{
            fontFamily: 'Cormorant Garamond, serif',
            fontSize: 40,
            color: toneColor(result.verdict.tone),
            fontWeight: 700,
            minWidth: 60,
            textAlign: 'right',
          }}>{result.total}</div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 12, alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: 11, fontFamily: 'Spline Sans Mono, monospace', color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
          Модифікатор
        </span>
        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-start' }}>
          {[-2, -1, 0, 1, 2, 3].map((m) => (
            <button key={m} onClick={() => setModifier(m)} style={modBtnStyle(modifier === m)}>
              {m > 0 ? `+${m}` : m}
            </button>
          ))}
        </div>

        <span style={{ fontSize: 11, fontFamily: 'Spline Sans Mono, monospace', color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
          Перевага
        </span>
        <div style={{ display: 'flex', gap: 4 }}>
          <button onClick={() => setAdv('disadvantage')} style={modBtnStyle(adv === 'disadvantage')}>–d6</button>
          <button onClick={() => setAdv('none')} style={modBtnStyle(adv === 'none')}>—</button>
          <button onClick={() => setAdv('advantage')} style={modBtnStyle(adv === 'advantage')}>+d6</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button onClick={roll} disabled={rolling} style={primaryBtn}>
          {rolling ? 'Кидаю…' : 'Roll 2d12'}
        </button>
        <button onClick={reset} style={ghostBtn}>Скинути</button>
      </div>

      <style>{`
        @keyframes die-tumble {
          0%   { transform: rotate(0deg) scale(1); }
          50%  { transform: rotate(180deg) scale(1.05); }
          100% { transform: rotate(360deg) scale(1); }
        }
        @keyframes die-settle {
          0%   { transform: scale(1.15) rotate(-8deg); }
          60%  { transform: scale(0.95) rotate(4deg); }
          100% { transform: scale(1) rotate(0deg); }
        }
      `}</style>
    </div>
  );
}

function Die({ value, type, rolling, small = false }: {
  value: number | null;
  type: 'hope' | 'fear' | 'adv';
  rolling: boolean;
  small?: boolean;
}): JSX.Element {
  const size = small ? 70 : 100;
  const palette = type === 'hope'
    ? { bg1: '#eccd8c', bg2: '#cca766', bg3: '#8c7444', stroke: '#5e4c20', face: '#2a1d10', glow: 'rgba(236,205,140,0.55)' }
    : type === 'fear'
      ? { bg1: '#5a4368', bg2: '#3a2842', bg3: '#21142a', stroke: '#0e0612', face: '#f0e6d4', glow: 'rgba(118,71,138,0.55)' }
      : { bg1: '#a6cfb5', bg2: '#79a98e', bg3: '#517564', stroke: '#1a3024', face: '#0d2418', glow: 'rgba(121,169,142,0.4)' };

  const cx = size / 2;
  const cy = size / 2;
  const hexR = size * 0.46;
  const hex: Array<[number, number]> = [];
  for (let i = 0; i < 6; i++) {
    const a = -Math.PI / 2 + (i * Math.PI) / 3;
    hex.push([cx + hexR * Math.cos(a), cy + hexR * Math.sin(a)]);
  }
  const hexPts = hex.map((p) => p.join(',')).join(' ');

  const innR = size * 0.30;
  const innPts: string[] = [];
  for (let i = 0; i < 5; i++) {
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / 5;
    innPts.push([cx + innR * Math.cos(a), cy + innR * Math.sin(a)].join(','));
  }

  return (
    <div style={{
      position: 'relative', width: size, height: size, display: 'inline-block',
      filter: `drop-shadow(0 0 ${rolling ? 18 : 8}px ${palette.glow})`,
      transition: 'filter .2s',
    }}>
      <div style={{
        width: size, height: size,
        animation: rolling
          ? 'die-tumble 0.45s linear infinite'
          : (value !== null ? 'die-settle 0.6s cubic-bezier(0.18, 1.5, 0.4, 1)' : 'none'),
        transformOrigin: 'center',
      }}>
        <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
          <defs>
            <radialGradient id={`die-bg-${type}`} cx="0.4" cy="0.35">
              <stop offset="0%" stopColor={palette.bg1} />
              <stop offset="55%" stopColor={palette.bg2} />
              <stop offset="100%" stopColor={palette.bg3} />
            </radialGradient>
          </defs>
          <polygon points={hexPts} fill={`url(#die-bg-${type})`} stroke={palette.stroke} strokeWidth="1.4" />
          <polygon points={innPts.join(' ')} fill="rgba(255,255,255,0.08)" stroke={palette.stroke} strokeWidth="1" />
          <text
            x={cx}
            y={cy + size * 0.06}
            textAnchor="middle"
            fontFamily="'Cormorant Garamond', 'Cormorant Garamond', serif"
            fontWeight={700}
            fontSize={size * 0.32}
            fill={palette.face}
          >{value ?? '?'}</text>
        </svg>
      </div>
      <div style={{
        position: 'absolute', bottom: -16, left: 0, right: 0,
        textAlign: 'center',
        fontFamily: 'Spline Sans Mono, monospace',
        fontSize: 9, color: palette.bg2,
        textTransform: 'uppercase', letterSpacing: '0.14em',
      }}>
        {type === 'hope' ? 'Hope' : type === 'fear' ? 'Fear' : 'D6'}
      </div>
    </div>
  );
}

const modBtnStyle = (active: boolean): React.CSSProperties => ({
  background: active ? 'var(--gold-dim)' : 'var(--bg-card)',
  color: active ? 'var(--bg-deep)' : 'var(--text-muted)',
  border: `1px solid ${active ? 'var(--gold)' : 'var(--border)'}`,
  borderRadius: 3,
  padding: '4px 10px',
  fontFamily: 'Spline Sans Mono, monospace',
  fontSize: 11,
  cursor: 'pointer',
  minWidth: 38,
});

const primaryBtn: React.CSSProperties = {
  flex: 1,
  background: 'linear-gradient(180deg, rgba(204,167,102,0.22), rgba(204,167,102,0.10))',
  color: 'var(--gold-bright)',
  border: '1px solid var(--gold-dim)',
  borderRadius: 4,
  padding: '10px 16px',
  fontFamily: 'Cormorant Garamond, serif',
  fontSize: 14,
  textTransform: 'uppercase',
  letterSpacing: '0.14em',
  fontWeight: 600,
  cursor: 'pointer',
};

const ghostBtn: React.CSSProperties = {
  background: 'transparent',
  color: 'var(--text-muted)',
  border: '1px solid var(--border)',
  borderRadius: 4,
  padding: '10px 16px',
  fontFamily: 'Cormorant Garamond, serif',
  fontSize: 13,
  textTransform: 'uppercase',
  letterSpacing: '0.14em',
  cursor: 'pointer',
};
